import type { PrefetchContext, PrefetchedDataProps } from 'src/shared/prefetch';
import type { SessionUser, SessionUserRole } from 'src/shared/session';

import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchPetitionEditPageDataFromApi, usePetitionEditPageDataFromApi } from './pageData';
import { prefetchPetitionSettingsPageDataFromApi, usePetitionSettingsPageDataFromApi } from './pageSettingsData';

export type PetitionEditPagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof usePetitionEditPageDataFromApi>[0];
	pageSettingsData: Parameters<typeof usePetitionSettingsPageDataFromApi>[0];
}>;

export type PetitionEditPageCombinedState = MergedState<
	ReturnType<typeof usePetitionEditPageDataFromApi>,
	ReturnType<typeof usePetitionSettingsPageDataFromApi>
>;

export function usePageContext(
	slugOrId: string,
	prefetchedData?: PetitionEditPagePrefetchedContext,
	includeUndiscoverableTags = false,
): PetitionEditPageCombinedState {
	return useMergedStates(
		usePetitionEditPageDataFromApi(prefetchedData?.pageData, { slugOrId, includeUndiscoverableTags }),
		usePetitionSettingsPageDataFromApi(prefetchedData?.pageSettingsData, { slugOrId }),
	);
}

export async function prefetchPageContext(context: PrefetchContext): Promise<PetitionEditPagePrefetchedContext> {
	const user = context.session?.user;
	const includeUndiscoverableTags = userHasRole(user, 'product') || userHasRole(user, 'campaigns');

	const [pageData, pageSettingsData] = await Promise.all([
		prefetchPetitionEditPageDataFromApi(context, {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			slugOrId: context.params.slug!,
			includeUndiscoverableTags,
		}),
		prefetchPetitionSettingsPageDataFromApi(context, {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			slugOrId: context.params.slug!,
		}),
	]);

	return { pageData, pageSettingsData };
}

function userHasRole(user: SessionUser | undefined | null, name: SessionUserRole): boolean {
	if (!user) return false;

	return user.roles[name];
}

export type PetitionEditPagePrefetchedDataProps = PrefetchedDataProps<PetitionEditPagePrefetchedContext>;
